/**
 * SocialPulse.blog sub-pages — News, Places, Publications, Writers.
 *
 * All share the BlogHome header/footer so navigation feels seamless.
 * Data comes from existing publisher_articles + locations collections;
 * empty states encourage the reader to become a writer.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapPin, ArrowRight, Newspaper, Users, BookMarked, Compass,
  TrendingUp, Sparkles, PenLine,
} from "lucide-react";
import BlogHome from "./BlogHome";
import BlogHeader from "@/components/blog/BlogHeader";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

// Compact wrapper that re-uses the shared BlogHeader on every sub-page
// (News, Places, Publications, Writers, Topics grid). One nav to rule
// them all — matches the header on BlogHome, BlogTopicHub, BlogQuestions.
function BlogPage({ children, testid, activeNav = "" }) {
  return (
    <div className="min-h-screen" style={{ background: "#FAFAF7", fontFamily: "Inter, system-ui, sans-serif" }} data-testid={testid}>
      <BlogHeader activeNav={activeNav} />
      <main className="max-w-[1400px] mx-auto px-6 md:px-10 pt-8 pb-16">
        {children}
      </main>
      <SlimFooter />
    </div>
  );
}

function SlimFooter() {
  return (
    <footer className="border-t py-8 text-center text-xs text-slate-400" style={{ borderColor: "rgba(15,23,42,0.05)" }}>
      © {new Date().getFullYear()} SocialPulse — Every Place Has a Story. Every Subject Has a Community.
    </footer>
  );
}

function PageHead({ overline, title, subtitle }) {
  return (
    <div className="mb-8">
      <p className="text-[11px] tracking-[0.24em] text-slate-500 uppercase mb-3">{overline}</p>
      <h1 style={{ fontFamily: "'Lora', serif", fontWeight: 600, fontSize: "clamp(2rem,4vw,3rem)", letterSpacing: "-0.02em", lineHeight: 1.1 }}>
        {title}
      </h1>
      {subtitle && <p className="mt-3 text-slate-600 text-lg max-w-2xl">{subtitle}</p>}
    </div>
  );
}

function ArticleCard({ a }) {
  return (
    <a href={`/blog/${a.slug}`} className="block bg-white rounded-2xl overflow-hidden border hover:shadow-md transition-shadow" style={{ borderColor: "rgba(15,23,42,0.06)" }} data-testid={`blog-card-${a.slug}`}>
      {a.cover_image_url && (
        <div className="aspect-[16/10] overflow-hidden">
          <img src={a.cover_image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
        </div>
      )}
      <div className="p-4">
        {a.primary_location_name && (
          <p className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">
            <MapPin className="w-3 h-3" style={{ color: "#f59e0b" }} />
            {a.primary_location_name}
          </p>
        )}
        <h3 className="text-lg text-slate-900 leading-snug" style={{ fontFamily: "'Lora', serif", fontWeight: 600 }}>{a.title}</h3>
        {a.summary && <p className="mt-1.5 text-sm text-slate-600 line-clamp-2">{a.summary}</p>}
      </div>
    </a>
  );
}


// ─── /blog/news ──────────────────────────────────────────────────

const NEWS_CATEGORIES = ["local-news", "community", "business", "health", "education", "nature"];

// Editorial captions shown under each stream heading — help visitors
// understand what a section expects before they read or contribute.
const NEWS_CAPTIONS = {
  "local-news":
    "Share timely, verified reports about local government, public services, transportation, public safety, development, elections and other matters affecting your area. Include the place, date, reliable sources and firsthand details whenever possible.",
  community:
    "Help your community stay informed by reporting local events, accidents, road conditions, closures, flooding, fires, disasters, missing-person notices, volunteer projects and neighborhood concerns. You may also share verified obituaries, memorials, community achievements and stories of local heroes; contact emergency services first when immediate danger exists.",
  business:
    "Introduce a local business, entrepreneur, product or service, or report a business opening, expansion, event or community contribution. Share accurate information about employment opportunities, innovations, challenges, customer experiences and developments affecting the local economy.",
  health:
    "Share reliable health education, wellness programs, healthcare resources, public-health notices and stories about people or organizations improving community well-being. Do not disclose private medical information or substitute an article for advice from a qualified healthcare professional.",
  education:
    "Write about schools, colleges, teachers, students, courses, scholarships, research, admissions, educational events and lifelong-learning opportunities. Celebrate achievements, identify community needs and share verified resources that can help learners of every age.",
  nature:
    "Document local wildlife, birds, plants, rivers, lakes, landscapes, weather events and environmental changes through stories, photographs and responsible observations. Include the date and general location, protect sensitive species, respect private property and never risk your safety to obtain a report.",
};

export function BlogNews() {
  const [streams, setStreams] = useState({});
  useEffect(() => {
    Promise.all(NEWS_CATEGORIES.map(async (cat) => {
      const r = await fetch(`${BASE}/articles?category=${cat}&limit=4`);
      if (r.ok) return [cat, await r.json()];
      return [cat, { articles: [] }];
    })).then((entries) => setStreams(Object.fromEntries(entries)));
  }, []);
  return (
    <BlogPage testid="blog-news-page" activeNav="news">
      <PageHead
        overline="News across the network"
        title="What the world is writing about today"
        subtitle="Community-reported news from every place and topic on SocialPulse. See something missing? Write it yourself."
      />
      <div className="mb-8 flex items-center gap-3 rounded-2xl bg-white p-4 border" style={{ borderColor: "rgba(15,118,110,0.15)" }}>
        <PenLine className="w-5 h-5 shrink-0" style={{ color: "#0F766E" }} />
        <p className="text-sm text-slate-700 flex-1">
          Everyone can write news. Add local reports, hyperlocal alerts, community wins, or a story you saw firsthand.
        </p>
        <a href="/publisher/write?category=local-news" className="rounded-full px-4 py-1.5 text-xs font-semibold text-white shrink-0" style={{ background: "#0F766E" }}>
          Write news
        </a>
      </div>
      <div className="space-y-10">
        {NEWS_CATEGORIES.map((cat) => {
          const list = streams[cat]?.articles || [];
          return (
            <section key={cat} data-testid={`blog-news-stream-${cat}`}>
              <div className="flex items-end justify-between mb-2">
                <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 600, fontSize: "1.5rem" }}>
                  {cat.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                </h2>
                <a href={`/blog/topics/${cat}`} className="text-sm font-semibold inline-flex items-center gap-1 shrink-0" style={{ color: "#0F766E" }}>
                  See all <ArrowRight className="w-4 h-4" />
                </a>
              </div>
              {NEWS_CAPTIONS[cat] && (
                <p
                  className="text-sm text-slate-600 mb-4 max-w-3xl leading-relaxed"
                  data-testid={`blog-news-caption-${cat}`}
                >
                  {NEWS_CAPTIONS[cat]}
                </p>
              )}
              {list.length === 0 ? (
                <div className="text-sm text-slate-500 bg-white rounded-xl p-4 border" style={{ borderColor: "rgba(15,23,42,0.06)" }}>
                  No stories yet for this stream. <a href="/publisher/write" className="font-semibold" style={{ color: "#0F766E" }}>Be the first to write one →</a>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {list.slice(0, 4).map((a) => <ArticleCard key={a.id} a={a} />)}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </BlogPage>
  );
}


// ─── /blog/places ────────────────────────────────────────────────

const TRENDING_PLACES = [
  { name: "Kerala, India",         id: "india/kerala" },
  { name: "Portland, Oregon",      id: "united-states/oregon/portland" },
  { name: "Kyoto, Japan",          id: "japan/kyoto" },
  { name: "Cape Town, South Africa", id: "south-africa/cape-town" },
  { name: "Rio de Janeiro, Brazil",  id: "brazil/rio-de-janeiro" },
  { name: "Copenhagen, Denmark",     id: "denmark/copenhagen" },
];

export function BlogPlaces() {
  const navigate = useNavigate();
  const [homeCity, setHomeCity] = useState(null);
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [level3, setLevel3] = useState("");
  const [level4, setLevel4] = useState("");
  const [opts, setOpts] = useState({ 0: [], 1: [], 2: [], 3: [] });
  const [loading, setLoading] = useState({ 0: false, 1: false, 2: false, 3: false });

  // Level 0 (countries) — loaded once
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem("sp_home_city") || window.localStorage.getItem("np_home_city");
      if (raw) setHomeCity(JSON.parse(raw));
    } catch { /* noop */ }
    setLoading((l) => ({ ...l, 0: true }));
    fetch(`${BASE}/places/children?parent=`)
      .then((r) => r.ok && r.json())
      .then((d) => setOpts((o) => ({ ...o, 0: d?.children || [] })))
      .finally(() => setLoading((l) => ({ ...l, 0: false })));
  }, []);

  // Level 1 (states) — reload when country changes
  useEffect(() => {
    setState(""); setLevel3(""); setLevel4("");
    setOpts((o) => ({ ...o, 1: [], 2: [], 3: [] }));
    if (!country) return;
    setLoading((l) => ({ ...l, 1: true }));
    fetch(`${BASE}/places/children?parent=${encodeURIComponent(country)}`)
      .then((r) => r.ok && r.json())
      .then((d) => setOpts((o) => ({ ...o, 1: d?.children || [] })))
      .finally(() => setLoading((l) => ({ ...l, 1: false })));
  }, [country]);

  // Level 2 (counties/districts) — reload when state changes
  useEffect(() => {
    setLevel3(""); setLevel4("");
    setOpts((o) => ({ ...o, 2: [], 3: [] }));
    if (!state) return;
    setLoading((l) => ({ ...l, 2: true }));
    fetch(`${BASE}/places/children?parent=${encodeURIComponent(state)}`)
      .then((r) => r.ok && r.json())
      .then((d) => setOpts((o) => ({ ...o, 2: d?.children || [] })))
      .finally(() => setLoading((l) => ({ ...l, 2: false })));
  }, [state]);

  // Level 3 (cities/villages) — reload when level3 changes
  useEffect(() => {
    setLevel4("");
    setOpts((o) => ({ ...o, 3: [] }));
    if (!level3) return;
    setLoading((l) => ({ ...l, 3: true }));
    fetch(`${BASE}/places/children?parent=${encodeURIComponent(level3)}`)
      .then((r) => r.ok && r.json())
      .then((d) => setOpts((o) => ({ ...o, 3: d?.children || [] })))
      .finally(() => setLoading((l) => ({ ...l, 3: false })));
  }, [level3]);

  const finalSlug = level4 || level3 || state || country;

  return (
    <BlogPage testid="blog-places-page" activeNav="explore-places">
      <PageHead
        overline="Explore places"
        title={homeCity ? `Places to discover — starting near ${homeCity.city || homeCity.name}` : "Explore every place — one dropdown at a time"}
        subtitle="Pick a country, then narrow down to state, district and city or village. Every level has stories, questions and a lively community waiting."
      />
      {homeCity && (
        <div className="mb-6 flex items-center gap-3 rounded-2xl bg-white p-4 border" style={{ borderColor: "rgba(15,118,110,0.15)" }}>
          <MapPin className="w-5 h-5 shrink-0" style={{ color: "#0F766E" }} />
          <div className="flex-1 min-w-0 text-sm text-slate-700">
            Your saved location: <strong>{homeCity.city || homeCity.name}{homeCity.state ? `, ${homeCity.state}` : ""}</strong>
          </div>
          <a href="/local" className="text-xs font-semibold" style={{ color: "#0F766E" }}>Change ›</a>
        </div>
      )}

      <section className="mb-10 rounded-2xl border bg-white p-6" style={{ borderColor: "rgba(15,23,42,0.06)" }} data-testid="blog-places-cascade">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <PlaceSelect
            label="Country"
            value={country}
            options={opts[0]}
            loading={loading[0]}
            onChange={setCountry}
            placeholder="Choose a country"
            testid="place-select-country"
          />
          <PlaceSelect
            label="State / Region"
            value={state}
            options={opts[1]}
            loading={loading[1]}
            onChange={setState}
            placeholder={country ? "Choose a state" : "Pick a country first"}
            disabled={!country}
            testid="place-select-state"
          />
          <PlaceSelect
            label="County / District"
            value={level3}
            options={opts[2]}
            loading={loading[2]}
            onChange={setLevel3}
            placeholder={state ? "Choose an area" : "Pick a state first"}
            disabled={!state}
            testid="place-select-district"
          />
          <PlaceSelect
            label="City / Village"
            value={level4}
            options={opts[3]}
            loading={loading[3]}
            onChange={setLevel4}
            placeholder={level3 ? "Choose a city" : "Pick a district first"}
            disabled={!level3}
            testid="place-select-city"
          />
        </div>

        <div className="mt-5 flex items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            Every level is browsable — you don&apos;t have to reach a specific city.
          </p>
          <button
            onClick={() => finalSlug && navigate(`/blog/places/${finalSlug}`)}
            disabled={!finalSlug}
            className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.02] disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: "#0F766E" }}
            data-testid="places-explore-btn"
          >
            Explore <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <section data-testid="blog-latest-articles">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5" style={{ color: "#0F766E" }} />
          <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 600, fontSize: "1.5rem" }}>Latest from every place</h2>
        </div>
        <LatestFromEveryPlace />
      </section>
    </BlogPage>
  );
}


function PlaceSelect({ label, value, options, loading, onChange, placeholder, disabled, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest font-bold text-slate-500">{label}</span>
      <div className="relative mt-1">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled || loading}
          className="w-full appearance-none rounded-xl bg-slate-50 border border-slate-200 px-3 py-2.5 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid={testid}
        >
          <option value="">{placeholder}</option>
          {options.map((o) => (
            <option key={o.slug} value={o.slug}>{o.name}</option>
          ))}
        </select>
        {loading && (
          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2">
            <span className="block w-3 h-3 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
          </span>
        )}
      </div>
    </label>
  );
}


function LatestFromEveryPlace() {
  const [recent, setRecent] = useState([]);
  useEffect(() => {
    fetch(`${BASE}/articles?limit=8`).then((r) => r.ok && r.json()).then((d) => setRecent(d?.articles || []));
  }, []);
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {recent.slice(0, 8).map((a) => <ArticleCard key={a.id} a={a} />)}
      {recent.length === 0 && (
        <div className="col-span-full text-sm text-slate-500 bg-white rounded-xl p-4 border" style={{ borderColor: "rgba(15,23,42,0.06)" }}>
          No stories yet. <a href="/publisher/write" className="font-semibold" style={{ color: "#0F766E" }}>Publish the first one →</a>
        </div>
      )}
    </div>
  );
}


// ─── /blog/publications ──────────────────────────────────────────

// Editors' picks — canonical publications we hand-feature. Each is a
// real published article at /blog/<slug>. Order controls display.
const FEATURED_PUBLICATIONS = [
  {
    slug:  "portland-or-community-guide",
    title: "Portland, Oregon — a community guide",
    kicker: "Editors' pick · Destination guide",
    blurb: "The Rose City from the ground up — its five quadrants, its independents, its rain, and the reasons people stay.",
    cover: "https://images.unsplash.com/photo-1503424886307-b090341d25d1?w=1200&q=80",
    accent: "#0F766E",
  },
  {
    slug:  "about-socialpulse-blog",
    title: "SocialPulse® and SocialPulse.blog — a manifesto",
    kicker: "Editorial · Manifesto",
    blurb: "Why we're building a place where every community, big or small, has an equal chance to be heard.",
    cover: "https://images.unsplash.com/photo-1483729558449-99ef09a8c325?w=1200&q=80",
    accent: "#B45309",
  },
];

export function BlogPublications() {
  const [latest, setLatest] = useState([]);
  useEffect(() => {
    fetch(`${BASE}/articles?limit=8`).then((r) => r.ok && r.json()).then((d) => setLatest(d?.articles || []));
  }, []);
  return (
    <BlogPage testid="blog-publications-page" activeNav="publications">
      <PageHead
        overline="Publications"
        title="Give your community a voice"
        subtitle="Publications are shared brands where writers, editors and communities publish together — like a magazine you can start in five minutes."
      />

      {/* Featured publications — real articles, editor-curated */}
      <section className="mb-12" data-testid="blog-featured-publications">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5" style={{ color: "#0F766E" }} />
          <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 600, fontSize: "1.5rem" }}>Editors' picks</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          {FEATURED_PUBLICATIONS.map((p) => (
            <a
              key={p.slug}
              href={`/blog/${p.slug}`}
              className="group block rounded-2xl overflow-hidden bg-white border hover:shadow-lg transition-shadow"
              style={{ borderColor: "rgba(15,23,42,0.06)" }}
              data-testid={`blog-featured-pub-${p.slug}`}
            >
              <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                <img src={p.cover} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-5">
                <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: p.accent }}>
                  {p.kicker}
                </p>
                <h3
                  className="mt-2 text-xl leading-snug"
                  style={{ fontFamily: "'Lora', serif", fontWeight: 600 }}
                >
                  {p.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 line-clamp-3">{p.blurb}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold" style={{ color: p.accent }}>
                  Read the piece <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* Start-a-publication CTA */}
      <section className="grid md:grid-cols-2 gap-6 mb-12">
        <div className="bg-white rounded-2xl p-6 border" style={{ borderColor: "rgba(15,23,42,0.06)" }}>
          <BookMarked className="w-8 h-8 mb-3" style={{ color: "#0F766E" }} />
          <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 600, fontSize: "1.4rem" }}>Start a publication</h2>
          <p className="mt-2 text-slate-600 text-sm">
            Portland Community Pulse. Kerala Heritage Stories. Farmers of the World. Anyone can create a publication for a city, school, family or subject.
          </p>
          <a href="/publisher/write" className="mt-4 inline-flex items-center gap-1 font-semibold" style={{ color: "#0F766E" }}>
            Create a publication <ArrowRight className="w-4 h-4" />
          </a>
        </div>
        <div className="bg-white rounded-2xl p-6 border" style={{ borderColor: "rgba(15,23,42,0.06)" }}>
          <Newspaper className="w-8 h-8 mb-3" style={{ color: "#0F766E" }} />
          <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 600, fontSize: "1.4rem" }}>Nominate a publication</h2>
          <p className="mt-2 text-slate-600 text-sm">
            Know a great community magazine, a local newsletter or an independent voice worth surfacing? Tell us — we&apos;ll feature it here.
          </p>
          <a href="/publisher/write?type=publication-nomination" className="mt-4 inline-flex items-center gap-1 font-semibold" style={{ color: "#0F766E" }}>
            Nominate one <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* Latest pieces */}
      <section data-testid="blog-latest-publications">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5" style={{ color: "#0F766E" }} />
          <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 600, fontSize: "1.5rem" }}>Latest pieces from the network</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {latest.slice(0, 8).map((a) => <ArticleCard key={a.id} a={a} />)}
          {latest.length === 0 && (
            <div className="col-span-full text-sm text-slate-500 bg-white rounded-xl p-4 border" style={{ borderColor: "rgba(15,23,42,0.06)" }}>
              Loading latest pieces…
            </div>
          )}
        </div>
      </section>
    </BlogPage>
  );
}


// ─── /blog/writers ───────────────────────────────────────────────

// ─── /blog/topics ────────────────────────────────────────────────

// Curated topics with per-topic identity: icon, color, invitation copy.
// Chosen to match the network's vertical presence — Farmers, Alumni,
// Family Trees, Memorials, etc. sit alongside broad publishing topics.
const TOPIC_CARDS = [
  { slug: "health",          label: "Health",              icon: "❤",  color: "#DC2626", tint: "#FEF2F2",
    blurb: "Share what a diagnosis, a doctor, a recipe or a walk taught you. Real stories change how people take care of themselves." },
  { slug: "education",       label: "Education",           icon: "📚", color: "#2563EB", tint: "#EFF6FF",
    blurb: "From village schools to global classrooms — write about a teacher, a course, a study hack, or a small experiment." },
  { slug: "finance",         label: "Finance",             icon: "💰", color: "#F59E0B", tint: "#FFFBEB",
    blurb: "Money, savings, taxes, first jobs, tiny businesses. What did nobody tell you that you had to learn the hard way?" },
  { slug: "food",            label: "Food",                icon: "🍲", color: "#EA580C", tint: "#FFF7ED",
    blurb: "Grandma's recipe, a street stall, a farm-to-table experiment. Food stories travel further than food itself." },
  { slug: "travel",          label: "Travel",              icon: "✈",  color: "#0284C7", tint: "#F0F9FF",
    blurb: "Not the guidebook. The moment. The neighbourhood. The person who changed your route. Tell us one." },
  { slug: "nature",          label: "Nature",              icon: "🌿", color: "#10B981", tint: "#ECFDF5",
    blurb: "A tree you know by name. A river you swim in. The season your city forgets. Nature is a subject, not a background." },
  { slug: "science",         label: "Science",             icon: "🔬", color: "#7C3AED", tint: "#F5F3FF",
    blurb: "Curious research, kitchen experiments, citizen astronomy. Explain something you love in plain language." },
  { slug: "weather",         label: "Weather",             icon: "⛅", color: "#0EA5E9", tint: "#F0F9FF",
    blurb: "Monsoons, blizzards, the day the sky went orange. Local weather memory is worth documenting — climate is downstream of stories." },
  { slug: "farmers",         label: "Farmers",             icon: "🌾", color: "#65A30D", tint: "#F7FEE7",
    blurb: "Notes from the field. Crop years, market prices, water, seeds, cooperatives. Farmers are journalists of the land." },
  { slug: "alumni",          label: "Alumni",              icon: "🎓", color: "#4F46E5", tint: "#EEF2FF",
    blurb: "Where did your batch scatter? What did school give you that you only understood later? Write it for the ones behind you." },
  { slug: "family-trees",    label: "Family Trees",        icon: "🌳", color: "#166534", tint: "#F0FDF4",
    blurb: "One story per grandparent is a gift. Names, migrations, recipes, occupations, silences — start the family record." },
  { slug: "memorials",       label: "Memorials",           icon: "🕯", color: "#78350F", tint: "#FEFCE8",
    blurb: "A page for someone you loved. A memory that shouldn't fade. Grief and celebration deserve a public home too." },
  { slug: "homes-real-estate", label: "Homes & Real Estate", icon: "🏠", color: "#7C2D12", tint: "#FEF3E4",
    blurb: "Renting, buying, building, restoring. Neighbourhood profiles. What the listings don't tell you." },
  { slug: "family-heritage", label: "Family & Heritage",   icon: "👨‍👩‍👧", color: "#B45309", tint: "#FFFBEB",
    blurb: "Traditions, rituals, festivals, dialects, family recipes. The culture inside your home is worth publishing." },
  { slug: "community",       label: "Community",           icon: "🤝", color: "#0F766E", tint: "#F0FDFA",
    blurb: "Local heroes, volunteer projects, neighbourhood repairs. Communities become visible when someone writes about them." },
  { slug: "local-news",      label: "Local News",          icon: "📰", color: "#334155", tint: "#F1F5F9",
    blurb: "Council meetings, potholes, market days, elections. Cover your street the way a national paper covers a capital." },
  { slug: "arts-culture",    label: "Arts & Culture",      icon: "🎨", color: "#DB2777", tint: "#FDF2F8",
    blurb: "Local artists, tiny theatres, gigs, bookshops, murals. If it moved you, it deserves a page." },
  { slug: "history",         label: "History",             icon: "🏛", color: "#78350F", tint: "#FEF3E4",
    blurb: "A place, a person, a struggle. Recover a story that was almost lost — write with sources and heart." },
  { slug: "faith-spirituality", label: "Faith & Spirituality",icon: "🙏", color: "#9333EA", tint: "#FAF5FF",
    blurb: "Prayer, doubt, practice, community. Faith stories are honest when they're specific — not preachy." },
  { slug: "technology",      label: "Technology",          icon: "💻", color: "#1E293B", tint: "#F8FAFC",
    blurb: "How you use a tool, what broke, what you built. Explain tech to your parents — they read too." },
  { slug: "business",        label: "Business",            icon: "📈", color: "#0F766E", tint: "#F0FDFA",
    blurb: "Small business, side project, employer story, market changes. Business is people; write about the people." },
];

// Session-scoped TTL for topic hub responses. Populated on the /blog/topics
// grid so that clicking into any topic hub renders instantly from cache
// (server also has a 60s in-memory cache — this is the second layer).
const HUB_CACHE_TTL_MS = 5 * 60 * 1000;

export function BlogTopics() {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({});      // { slug: n_articles }
  const [featured, setFeatured] = useState({});  // { slug: {slug,title,summary,cover_image_url,...} }

  // Preload each topic's hub payload (limit=6) into sessionStorage so that
  // navigating into a topic hub is instant on repeat visits. Also lets us
  // showcase the topic's first seeded article right on the grid card.
  useEffect(() => {
    let alive = true;
    (async () => {
      const entries = await Promise.all(
        TOPIC_CARDS.map(async (t) => {
          const cacheKey = `blog:hub:${t.slug}`;
          // Cache hit — reuse without hitting the network
          try {
            const raw = sessionStorage.getItem(cacheKey);
            if (raw) {
              const cached = JSON.parse(raw);
              if (Date.now() - cached.ts < HUB_CACHE_TTL_MS && Array.isArray(cached.articles)) {
                return [t.slug, cached.articles];
              }
            }
          } catch { /* ignore parse errors */ }
          try {
            const r = await fetch(`${BASE}/hub/topic/${encodeURIComponent(t.slug)}?limit=6`);
            if (!r.ok) return [t.slug, null];
            const d = await r.json();
            const articles = d.articles || [];
            try {
              sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), articles }));
            } catch { /* quota — ignore */ }
            return [t.slug, articles];
          } catch {
            return [t.slug, null];
          }
        }),
      );
      if (!alive) return;
      const cn = {}; const feat = {};
      entries.forEach(([slug, articles]) => {
        if (articles === null) { cn[slug] = null; return; }
        cn[slug] = articles.length;
        if (articles.length > 0) feat[slug] = articles[0];
      });
      setCounts(cn);
      setFeatured(feat);
    })();
    return () => { alive = false; };
  }, []);

  return (
    <BlogPage testid="blog-topics-page" activeNav="explore-topics">
      <PageHead
        overline="Explore topics"
        title="Every Subject Has a Community"
        subtitle="Choose a topic and start writing — even a 200-word post seeds a whole community. Every topic below is looking for its first authoritative local voice."
      />

      <div
        className="mb-10 rounded-3xl p-6 md:p-8 border flex flex-col md:flex-row items-start md:items-center gap-4"
        style={{ borderColor: "rgba(15,118,110,0.15)", background: "linear-gradient(135deg,#F0FDFA 0%,#FFFFFF 100%)" }}
        data-testid="blog-topics-writer-cta"
      >
        <div className="w-12 h-12 rounded-full grid place-items-center shrink-0" style={{ background: "linear-gradient(135deg,#0F766E,#134E4A)" }}>
          <PenLine className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-xl" style={{ fontFamily: "'Lora', serif", fontWeight: 600 }}>
            Which topics do you know best?
          </h3>
          <p className="mt-1 text-slate-600 text-sm">
            Nature, food, farming, family history — everyone is an expert in something. Pick one and publish your first short piece today.
          </p>
        </div>
        <button
          onClick={() => navigate("/publisher/write")}
          className="rounded-full px-5 py-2.5 text-sm font-semibold text-white shrink-0"
          style={{ background: "#0F766E" }}
          data-testid="blog-topics-start-writing"
        >
          Start writing ›
        </button>
      </div>

      <section data-testid="blog-topics-grid">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {TOPIC_CARDS.map((t) => {
            const n = counts[t.slug];
            const isEmpty = n === 0;
            const feat = featured[t.slug];
            const go = () => navigate(`/blog/topics/${t.slug}`);
            return (
              <div
                key={t.slug}
                onClick={go}
                role="link"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); go(); } }}
                className="rounded-2xl p-5 border transition-all hover:-translate-y-0.5 hover:shadow-lg group relative overflow-hidden cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{ borderColor: "rgba(15,23,42,0.06)", background: t.tint }}
                data-testid={`blog-topic-card-${t.slug}`}
              >
                {/* Corner glyph */}
                <div
                  className="absolute -top-4 -right-3 text-6xl opacity-15 select-none pointer-events-none"
                  style={{ color: t.color, fontFamily: "'Lora', serif" }}
                  aria-hidden="true"
                >
                  {t.icon}
                </div>

                <div
                  className="w-11 h-11 rounded-xl grid place-items-center text-xl mb-3 relative z-10"
                  style={{ background: t.color, color: "#ffffff", boxShadow: `0 4px 12px ${t.color}33` }}
                >
                  {t.icon}
                </div>
                <h3
                  className="text-xl relative z-10"
                  style={{ fontFamily: "'Lora', serif", fontWeight: 600, color: "#0F172A" }}
                >
                  {t.label}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed relative z-10">
                  {t.blurb}
                </p>

                {feat && (
                  <div
                    className="mt-4 rounded-xl bg-white/70 backdrop-blur-sm border p-3 relative z-10"
                    style={{ borderColor: `${t.color}33` }}
                    data-testid={`blog-topic-featured-${t.slug}`}
                  >
                    <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: t.color }}>
                      Featured story
                    </div>
                    <div
                      className="mt-1 text-[15px] font-semibold text-slate-900 line-clamp-2"
                      style={{ fontFamily: "'Lora', serif" }}
                    >
                      {feat.title}
                    </div>
                  </div>
                )}

                <div className="mt-4 flex items-center justify-between gap-3 relative z-10">
                  <span
                    className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 group-hover:text-slate-950 uppercase tracking-widest"
                    data-testid={`blog-topic-read-${t.slug}`}
                  >
                    {isEmpty ? "Be the first ›" : n ? `Read ${n} stor${n === 1 ? "y" : "ies"} ›` : "Explore ›"}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/publisher/write?topic=${encodeURIComponent(t.slug)}`); }}
                    className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ background: t.color }}
                    data-testid={`blog-topic-write-${t.slug}`}
                  >
                    Write about {t.label.split(" ")[0]} ›
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Bottom CTA — soft invitation to suggest new topics */}
      <div
        className="mt-12 rounded-2xl border p-6 text-center"
        style={{ borderColor: "rgba(15,23,42,0.06)", background: "#ffffff" }}
        data-testid="blog-topics-suggest"
      >
        <p className="text-sm text-slate-600">
          Don't see a topic you want to write about?{" "}
          <a href="/blog/topics/suggest" className="font-semibold" style={{ color: "#0F766E" }}>
            Suggest a new one ›
          </a>
        </p>
      </div>
    </BlogPage>
  );
}


// ─── /blog/writers ───────────────────────────────────────────────

export function BlogWriters() {
  return (
    <BlogPage testid="blog-writers-page" activeNav="writers">
      <PageHead
        overline="Writers"
        title="Voices worth following"
        subtitle="Local writers, subject experts, community organizations, teachers, farmers, travelers, historians, students and seniors. Not ranked by follower count — ranked by helpfulness, originality and community trust."
      />
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 border" style={{ borderColor: "rgba(15,23,42,0.06)" }}>
          <Users className="w-8 h-8 mb-3" style={{ color: "#0F766E" }} />
          <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 600, fontSize: "1.4rem" }}>Become a writer</h2>
          <p className="mt-2 text-slate-600 text-sm">
            Sign up, tell us the places and topics you know, and start publishing. Your work reaches readers across the whole SocialPulse network.
          </p>
          <a href="/publisher/write" className="mt-4 inline-flex items-center gap-1 font-semibold" style={{ color: "#0F766E" }}>
            Start writing <ArrowRight className="w-4 h-4" />
          </a>
        </div>
        <div className="bg-white rounded-2xl p-6 border" style={{ borderColor: "rgba(15,23,42,0.06)" }}>
          <Newspaper className="w-8 h-8 mb-3" style={{ color: "#0F766E" }} />
          <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 600, fontSize: "1.4rem" }}>Featured writers</h2>
          <p className="mt-2 text-slate-600 text-sm">
            Curated writer showcase pages are on the roadmap. Once a writer has 3+ published stories with community reactions, they'll appear here.
          </p>
          <p className="mt-3 text-[11px] text-slate-400 uppercase tracking-widest">Coming soon</p>
        </div>
      </div>
    </BlogPage>
  );
}
